import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import experiments from '@reverbdotcom/commons/src/experiments';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { isPayLaterSupported, KLARNA_PAY_LATER_COUNTRIES } from './klarnaOnSiteMessaging';

const KLARNA_ACCOUNT = 'klarna_account';
const KLARNA_PAY_LATER = 'klarna_pay_later';

const ALL_EXPERIMENT_KLARNA_COUNTRIES = [
  ...KLARNA_PAY_LATER_COUNTRIES,
  'CA',
];

const KLARNA_PAYMENT_METHODS = new Set([
  KLARNA_ACCOUNT,
  KLARNA_PAY_LATER,
]);

export function isKlarnaAccountAvailable(acceptedPaymentMethods: string[], user: Partial<IUser>) {
  if (!acceptedPaymentMethods) {
    return false;
  }

  return acceptedPaymentMethods.includes(KLARNA_ACCOUNT) &&
    isKlarnaAvailableForUserCountry(user);
}

export function isKlarnaPayLaterAvailable(acceptedPaymentMethods: string[], user: Partial<IUser>) {
  if (!acceptedPaymentMethods) {
    return false;
  }

  return acceptedPaymentMethods.includes(KLARNA_PAY_LATER) &&
    isKlarnaAvailableForUserCountry(user);
}

export function canTrackKlarnaCanadaExperiment(acceptedPaymentMethods: string[], user: Partial<IUser>) {
  if (!acceptedPaymentMethods) { return false; }

  return acceptedPaymentMethods.includes(KLARNA_ACCOUNT) &&
    user.countryCode === 'CA';
}

export function canTrackKlarnaPayLaterExperiment(acceptedPaymentMethods: string[], user: Partial<IUser>) {
  if (!acceptedPaymentMethods) { return false; }

  return acceptedPaymentMethods.includes(KLARNA_PAY_LATER) &&
    isPayLaterSupported(user.countryCode);
}

export function filterBuyerPaymentMethods(acceptedPaymentMethods: string[], user: Partial<IUser>) {
  if (!acceptedPaymentMethods) {
    return [];
  }

  function isPaymentMethodAvailable(paymentMethod: string) {
    if (KLARNA_PAYMENT_METHODS.has(paymentMethod) && !isKlarnaAvailableForUserCountry(user)) {
      return false;
    }
    return true;
  }

  return acceptedPaymentMethods.filter(isPaymentMethodAvailable);
}

/**
 * Klarna is available for all existing countries where it's offered such as Great Britain
 *
 * It is conditionally available for Canadian users in the {@link experiments.KLARNA_CANADA} rollout.
 *
 * It is also conditionally available for {@link KLARNA_PAY_LATER_COUNTRIES} countries
 * behind the {@link experiments.KLARNA_PAY_LATER} rollout.
 */
export function isKlarnaAvailableForUserCountry(user: Partial<IUser>) {
  if (!ALL_EXPERIMENT_KLARNA_COUNTRIES.includes(user.countryCode)) {
    return true;
  }

  const isKlarnaAccount = user.countryCode === 'CA' &&
    isExperimentEnabled(user, experiments.KLARNA_CANADA);

  const isKlarnaPayLater = isPayLaterSupported(user.countryCode) &&
    isExperimentEnabled(user, experiments.KLARNA_PAY_LATER);

  return isKlarnaAccount || isKlarnaPayLater;
}

export function hasPaymentMethodViaKlarna(acceptedPaymentMethods: string[]) {
  if (!acceptedPaymentMethods) {
    return false;
  }

  return acceptedPaymentMethods.some((paymentMethod) => KLARNA_PAYMENT_METHODS.has(paymentMethod));
}
